import {
  ALERT_CATEGORIES,
  alertSubjectLine,
  ALERT_CATEGORY_LABEL,
  MONITOR_CATALOG,
  TICKET_STATE_LABEL,
  can,
  localDateOf,
  type AlertBand,
  type AlertBoardView,
  type AlertCategory,
  type AlertEventType,
  type AlertMuteChoice,
  type AlertSnoozeChoice,
  type AlertSubjectType,
  type MonitorKey,
  type MonitorNotMonitoredReason,
  type Role,
  type TicketState,
} from '@servwell/shared';
import { dateOnly, timeInTz } from '../../lib/format';
import { ago } from '../schedule/crewAsOf';
import { shopTime } from '../settings/monitorsView';
import type { Tone } from '../ui/Badge';

/**
 * The Alerts board's rules (ops insights Phase 7 spec §6), pure, so each is a test: what its
 * address may say, what a card and the panel say about an alert, where its primary link goes,
 * and what an empty board MEANS. No hooks, no React.
 *
 * ⚠️ AN EMPTY BOARD IS NEVER ONE SENTENCE. "No monitor is Live yet", "some monitors are not
 * being checked" and "every Live monitor checked and found nothing" are three different facts,
 * and a failed read is a fourth (the page's `ErrorState`). Only the last one is an all-clear
 * (SONAR's "invisible withholding" lesson; 6b's fail-closed health).
 */

// ── The address ─────────────────────────────────────────────────────────────────────────

/** `all` is the default and is never written: `/alerts` is the board. */
export type AlertsSearch = {
  view?: Exclude<AlertBoardView, 'all'>;
  /** The column a phone shows (one at a time). */
  col?: AlertCategory;
  /** The open alert: its panel is open and its card marked. */
  alert?: string;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * `/alerts`' `validateSearch` (the `tabSearch` contract): a value the page can use is kept;
 * junk, absence and the defaults are left out, so a link has one spelling. `?alert=` must be an
 * id (a deep link from the bell, the morning email or MCP); anything else opens nothing.
 */
export function alertsSearch(raw: Record<string, unknown>): AlertsSearch {
  const out: AlertsSearch = {};
  if (raw.view === 'mine' || raw.view === 'snoozed') out.view = raw.view;
  if (
    typeof raw.col === 'string' &&
    (ALERT_CATEGORIES as readonly string[]).includes(raw.col)
  ) {
    out.col = raw.col as AlertCategory;
  }
  if (typeof raw.alert === 'string' && UUID.test(raw.alert)) out.alert = raw.alert;
  return out;
}

export const ALERT_VIEW_TABS: readonly { key: AlertBoardView; label: string }[] = [
  { key: 'all', label: 'Waiting' },
  { key: 'mine', label: 'Mine' },
  { key: 'snoozed', label: 'Snoozed' },
];

// ── Who ─────────────────────────────────────────────────────────────────────────────────

/** Who sees the board and its count: whoever reads alerts (the owner and the office). */
export const mayReadAlerts = (role: Role): boolean => can(role, 'alert', 'read');
/** Who takes, snoozes, resolves and mutes. */
export const mayWorkAlerts = (role: Role): boolean => can(role, 'alert', 'work');
/** Who reaches Settings → Alerts (the owner). */
export const mayConfigureAlerts = (role: Role): boolean =>
  can(role, 'alert', 'configure');

/** The count's text on a small badge: nothing at none, the count up to 99, then "99+". */
export function countBadge(n: number | undefined): string | null {
  if (!n || n <= 0) return null;
  return n > 99 ? '99+' : String(n);
}

/** What the count SAYS (a badge is drawn, not read). */
export function countWords(n: number | undefined): string | null {
  if (!n || n <= 0) return null;
  return n === 1 ? '1 alert needs someone' : `${n} alerts need someone`;
}

// ── Bands, ages, states ─────────────────────────────────────────────────────────────────

export const BAND_LABEL: Record<AlertBand, string> = {
  critical: 'Critical',
  high: 'High',
  normal: 'Normal',
};

export const BAND_TONE: Record<AlertBand, Tone> = {
  critical: 'danger',
  high: 'warn',
  normal: 'neutral',
};

/** The card's left stripe, a token per band (no hex: light and dark both redefine them). */
export const BAND_STRIPE: Record<AlertBand, string> = {
  critical: 'bg-[var(--sw-danger-text)]',
  high: 'bg-[var(--sw-warn-text)]',
  normal: 'bg-[var(--sw-muted)]',
};

/** "Just now", "52 min ago", "3 h ago", "2 days ago": since it was first raised. */
export function ageLabel(firstFiredAt: Date | string, now: number): string {
  const ms = now - new Date(firstFiredAt).getTime();
  return ms < 60_000 ? 'Just now' : ago(ms);
}

const isSnoozed = (snoozedUntil: Date | string | null, now: number): boolean =>
  snoozedUntil !== null && new Date(snoozedUntil).getTime() > now;

/** "Thu Oct 9, 7:00 AM" on the shop's clock. */
export function untilLabel(at: Date | string, tz: string): string {
  const day = dateOnly(localDateOf(new Date(at), tz), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return `${day}, ${timeInTz(at, tz)}`;
}

/** The panel's state chip: Resolved, Snoozed · <when>, Taken or New. */
export function stateChip(
  a: { status: string; snoozedUntil: Date | string | null },
  tz: string,
  now: number,
): { label: string; tone: Tone } {
  if (a.status === 'resolved') return { label: 'Resolved', tone: 'success' };
  if (isSnoozed(a.snoozedUntil, now)) {
    return { label: `Snoozed · ${untilLabel(a.snoozedUntil!, tz)}`, tone: 'info' };
  }
  if (a.status === 'taken') return { label: 'Taken', tone: 'brand' };
  return { label: 'New', tone: 'warn' };
}

/** Where an alert came from: the check that raised it. */
export function sourceLabel(key: MonitorKey): string {
  return MONITOR_CATALOG[key].cadence === 'nightly'
    ? 'Nightly check · 6:15 AM'
    : 'Hourly check';
}

// ── What it is about ────────────────────────────────────────────────────────────────────

export type Evidence = Record<string, string | number | boolean | null>;

/**
 * The card's subject line ("#42 Leaking valve", "Capacitor 45/5 · Van 1"): the shared
 * `alertSubjectLine`, which the morning email says too, so the card and the email never drift.
 */
export const subjectLine = alertSubjectLine;

/** Each evidence key's words in "Why this fired"; a key not here reads as itself. */
const EVIDENCE_LABEL: Record<string, string> = {
  title: 'Title',
  status: 'Status',
  replyDueAt: 'Reply was due',
  item: 'Part',
  location: 'Where',
  quantity: 'Count',
  planNumber: 'Plan',
  planName: 'Plan name',
  overdueSince: 'Overdue since',
  overdueCount: 'Visits overdue',
  receivedAt: 'Came in',
  urgency: 'Urgency',
  customer: 'Customer',
  sentAt: 'Sent',
  lastFollowUpAt: 'Last follow-up',
  lastVisitAt: 'Last visit',
  visitStartAt: 'Visit starts',
  shortLines: 'Short',
  moreLines: 'More short lines',
  onHand: 'On hand',
  onOrder: 'On order',
  keepAtLeast: 'Keep at least',
  planned: 'Planned in the window',
};

/** What "number" means, by subject. */
const NUMBER_LABEL: Partial<Record<AlertSubjectType, string>> = {
  ticket: 'Ticket',
  request: 'Request',
  quote: 'Quote',
  job: 'Job',
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const INSTANT = /^\d{4}-\d{2}-\d{2}T/;

function valueText(key: string, v: string | number | boolean, tz: string, now: Date) {
  if (key === 'number' || key === 'planNumber') return `#${v}`;
  if (key === 'quantity' && typeof v === 'number' && v < 0) {
    // A true minus sign: "−2" is a count below zero, "-2" reads as a dash.
    return `−${Math.abs(v)}`;
  }
  if (key === 'status' && typeof v === 'string' && v in TICKET_STATE_LABEL) {
    return TICKET_STATE_LABEL[v as TicketState];
  }
  if (key === 'urgency' && typeof v === 'string') {
    return v.charAt(0).toUpperCase() + v.slice(1);
  }
  if (typeof v === 'string' && DATE_ONLY.test(v)) return dateOnly(v);
  if (typeof v === 'string' && INSTANT.test(v) && !Number.isNaN(Date.parse(v))) {
    return shopTime(v, tz, now);
  }
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

/** "Why this fired": the rule, then each piece of evidence in words (shop time for times). */
export function evidenceRows(
  a: { subject: { type: AlertSubjectType }; evidence: Evidence; description: string },
  tz: string,
  now: Date,
): { label: string; value: string }[] {
  const rows = [{ label: 'Rule', value: a.description }];
  for (const [key, v] of Object.entries(a.evidence)) {
    if (v === null || v === '') continue;
    // `job_short` always writes `moreLines` (0 when every short line is listed): the row only
    // when there are more, never "More short lines 0" (p7 QA).
    if (key === 'moreLines' && !(Number(v) > 0)) continue;
    const label =
      key === 'number'
        ? (NUMBER_LABEL[a.subject.type] ?? 'Number')
        : (EVIDENCE_LABEL[key] ?? key);
    rows.push({ label, value: valueText(key, v, tz, now) });
  }
  return rows;
}

// ── Where it is fixed ───────────────────────────────────────────────────────────────────

/** The panel's primary action: the real screen where the problem is fixed. */
export interface PrimaryLink {
  label: string;
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
}

export function primaryLink(a: {
  subject: { type: AlertSubjectType; id: string };
  evidence: Evidence;
}): PrimaryLink {
  const { type, id } = a.subject;
  const n = a.evidence.number;
  switch (type) {
    case 'ticket':
      return {
        label: n != null ? `Open ticket #${n}` : 'Open the ticket',
        to: '/tickets',
        search: { ticket: id },
      };
    case 'stock':
      return { label: 'Open inventory', to: '/inventory' };
    case 'agreement':
      return {
        label: 'Open the plan',
        to: '/agreements/$agreementId',
        params: { agreementId: id },
      };
    case 'request':
      return { label: 'Open requests', to: '/requests' };
    case 'quote':
      return {
        label: n != null ? `Open quote #${n}` : 'Open the quote',
        to: '/quotes/$quoteId',
        params: { quoteId: id },
      };
    case 'customer':
      return {
        label: 'Open the customer',
        to: '/customers/$customerId',
        params: { customerId: id },
      };
    case 'job':
      return {
        label: "Open the job's parts",
        to: '/jobs/$jobId',
        params: { jobId: id },
        search: { tab: 'work' },
      };
  }
}

/**
 * Resolve… by hand: never on a ticket alert (Giuliano, 2026-10-07). The ticket is where the
 * fix and its root cause are written, and resolving the ticket clears its alert.
 */
export const mayResolveByHand = (subjectType: AlertSubjectType): boolean =>
  subjectType !== 'ticket';

// ── The header and the empty board ──────────────────────────────────────────────────────

type BoardFacts = {
  view: AlertBoardView;
  needSomeone: number;
  open: number;
  lastCheckedAt: Date | string | null;
  liveMonitors: number;
  notMonitored: readonly {
    key: MonitorKey;
    label: string;
    reason: MonitorNotMonitoredReason;
  }[];
  columns: readonly { cards: readonly unknown[] }[];
};

/**
 * "3 need someone, 2 urgent · 7 open · last checked 10:00 AM".
 *
 * `urgent` is the SIDEBAR's number, the same read (`alert.count`: critical and high, Live, New,
 * not snoozed, not muted), so the header and the pill can never disagree; "need someone" counts
 * every band. Left out while that read has no answer (never a guessed 0).
 */
export function headerLine(
  b: BoardFacts,
  tz: string,
  now: Date,
  urgent?: number,
): string {
  if (b.liveMonitors === 0) return 'No monitor is Live yet';
  const checked = b.lastCheckedAt
    ? `last checked ${shopTime(b.lastCheckedAt, tz, now)}`
    : 'no check yet';
  const n = (x: number) => x.toLocaleString('en-US');
  const need =
    urgent === undefined
      ? `${n(b.needSomeone)} need someone`
      : `${n(b.needSomeone)} need someone, ${n(urgent)} urgent`;
  return `${need} · ${n(b.open)} open · ${checked}`;
}

const NOT_MONITORED_WHY: Record<MonitorNotMonitoredReason, string> = {
  never_ran: 'it has never run',
  failed: 'its last check failed',
  overdue: 'no check has finished on time',
  disabled: 'it is turned off',
};

/** One line of the health banner: "Not monitored: <label> — <why>". */
export function notMonitoredLine(m: {
  label: string;
  reason: MonitorNotMonitoredReason;
}): string {
  return `Not monitored: ${m.label} — ${NOT_MONITORED_WHY[m.reason]}.`;
}

export type EmptyBoard = {
  /** Which of the empty answers this is (a test reads it; the words are for people). */
  kind: 'none_live' | 'mine' | 'snoozed' | 'partly_blind' | 'all_clear';
  title: string;
  description: string;
};

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * What a board with no cards in this view means; null when it has cards. Only `all_clear`
 * claims that nothing is wrong, and only when every Live monitor is being checked.
 */
export function emptyBoard(b: BoardFacts, tz: string, now: Date): EmptyBoard | null {
  if (b.columns.some((c) => c.cards.length > 0)) return null;
  if (b.liveMonitors === 0) {
    return {
      kind: 'none_live',
      title: 'No monitor is Live yet',
      description:
        'Every monitor is still in Testing, so nothing reaches this board. The owner reviews what each one found in Settings and switches it Live when it is useful.',
    };
  }
  if (b.view === 'mine') {
    return {
      kind: 'mine',
      title: 'Nothing is yours',
      description: 'Alerts you take, or that are assigned to you, show here.',
    };
  }
  if (b.view === 'snoozed') {
    return {
      kind: 'snoozed',
      title: 'Nothing is snoozed',
      description:
        'A snoozed alert waits here until its time comes, then goes back on the board.',
    };
  }
  const snoozedNote =
    b.open > 0
      ? ` ${b.open} ${plural(b.open, 'alert is', 'alerts are')} snoozed (see Snoozed).`
      : '';
  if (b.notMonitored.length > 0) {
    const n = b.notMonitored.length;
    return {
      kind: 'partly_blind',
      title: 'Nothing found by the monitors that are checking',
      description: `${n} Live ${plural(n, 'monitor is', 'monitors are')} not being checked right now (named above), so this is not an all-clear.${snoozedNote}`,
    };
  }
  const when = b.lastCheckedAt ? ` at ${shopTime(b.lastCheckedAt, tz, now)}` : '';
  return {
    kind: 'all_clear',
    title: 'All clear',
    description: `Every Live monitor checked${when} and found nothing waiting.${snoozedNote}`,
  };
}

/** The column a phone shows: the open alert's, else the address's, else the first with cards. */
export function phoneColumn(
  columns: readonly { category: AlertCategory; cards: readonly { id: string }[] }[],
  chosen: AlertCategory | undefined,
  openId: string | undefined,
): AlertCategory {
  const open = openId
    ? columns.find((c) => c.cards.some((x) => x.id === openId))?.category
    : undefined;
  return (
    open ??
    chosen ??
    columns.find((c) => c.cards.length > 0)?.category ??
    ALERT_CATEGORIES[0]
  );
}

export const categoryLabel = (c: AlertCategory): string => ALERT_CATEGORY_LABEL[c];

// ── The panel's choices and history ─────────────────────────────────────────────────────

export const SNOOZE_CHOICES: readonly { choice: AlertSnoozeChoice; label: string }[] = [
  { choice: 'tomorrow', label: 'Until tomorrow 7 AM' },
  { choice: 'three_days', label: 'For 3 days' },
  { choice: 'monday', label: 'Until Monday 7 AM' },
  { choice: 'date', label: 'Until a date…' },
];

export const MUTE_CHOICES: readonly { choice: AlertMuteChoice; label: string }[] = [
  { choice: 'thirty_days', label: '30 days' },
  { choice: 'ninety_days', label: '90 days' },
  { choice: 'date', label: 'Until a date' },
];

/** A person's name, or how to say who when there is none. */
type NameOf = (id: string) => string | null;

const BY_THE_CHECK: Partial<Record<AlertEventType, string>> = {
  fired: 'Raised by the check',
  updated: 'The check found new details',
  auto_resolved: 'Cleared: the check no longer finds it',
  unlatched:
    'The check no longer finds it: if the problem comes back, it will be raised again',
  escalated: 'Sent to the owner: nobody took it in time',
  went_live: 'Its monitor went Live',
  went_testing: 'Its monitor went back to Testing',
};

/** One line of the panel's history, in words. */
export function historyLine(
  e: {
    type: AlertEventType;
    actor: { id: string; name: string | null } | null;
    payload: Record<string, string | number | boolean | null>;
  },
  nameOf: NameOf,
  tz: string,
): string {
  const who = e.actor ? (e.actor.name ?? 'Former staff') : null;
  const p = e.payload;
  const until = typeof p.until === 'string' ? untilLabel(p.until, tz) : null;
  switch (e.type) {
    case 'taken':
      return `${who ?? 'Someone'} took it`;
    case 'assigned': {
      const to =
        typeof p.ownerId === 'string' ? (nameOf(p.ownerId) ?? 'someone') : 'someone';
      return `${who ?? 'Someone'} assigned it to ${to}`;
    }
    case 'unassigned':
      return `${who ?? 'Someone'} put it back in the office queue`;
    case 'snoozed':
      return `${who ?? 'Someone'} snoozed it${until ? ` until ${until}` : ''}`;
    case 'unsnoozed':
      return p.reason === 'band_rose'
        ? 'Back on the board: it became more urgent'
        : `${who ?? 'Someone'} put it back on the board`;
    case 'human_resolved':
      return `${who ?? 'Someone'} resolved it${typeof p.note === 'string' ? `: ${p.note}` : ''}`;
    case 'band_changed':
      return typeof p.to === 'string' && p.to in BAND_LABEL
        ? `Now ${BAND_LABEL[p.to as AlertBand].toLowerCase()}`
        : 'Its urgency changed';
    case 'muted':
      return `${who ?? 'Someone'} muted it${until ? ` until ${until}` : ''}${typeof p.reason === 'string' ? `: ${p.reason}` : ''}`;
    case 'unmuted':
      return `${who ?? 'Someone'} lifted the mute`;
    case 'ticket_opened':
      return `${who ?? 'Someone'} opened a ticket`;
    case 'judged':
      return `${who ?? 'Someone'} judged it ${p.useful === true ? 'useful' : 'not useful'}`;
    default:
      return BY_THE_CHECK[e.type] ?? e.type;
  }
}
